import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, Clock, CalendarDays, ClipboardList, AlertTriangle, BookOpen, BarChart3, ArrowRight } from 'lucide-react';
import { formatDateLong, formatDateShort } from '@/utils/dateFormat';
import { SchoolzestAIWidget } from '@/components/ui/SchoolzestAIWidget';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const SUBJECT_COLORS = {
  'Mathematics': 'bg-blue-100 text-blue-800 border-blue-200',
  'English': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Science': 'bg-purple-100 text-purple-800 border-purple-200',
  'History': 'bg-amber-100 text-amber-800 border-amber-200',
  'Geography': 'bg-teal-100 text-teal-800 border-teal-200',
  'Art': 'bg-pink-100 text-pink-800 border-pink-200',
  'Music': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'Physical Education': 'bg-orange-100 text-orange-800 border-orange-200',
  'default': 'bg-slate-100 text-slate-700 border-slate-200'
};

const getSubjectColor = (subject) => {
  for (const [key, value] of Object.entries(SUBJECT_COLORS)) {
    if (key !== 'default' && subject?.toLowerCase().includes(key.toLowerCase())) return value;
  }
  return SUBJECT_COLORS.default;
};

const TeacherPortal = () => {
  const { token, user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodsPerDay, setPeriodsPerDay] = useState(8);
  const [drilldown, setDrilldown] = useState({ open: false, card: null });
  const openDrill = (card) => setDrilldown({ open: true, card });

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const periods = Array.from({ length: periodsPerDay }, (_, i) => `Period ${i + 1}`);

  useEffect(() => {
    fetchPortalData();
    axios.get(`${API}/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data?.periods_per_day) setPeriodsPerDay(res.data.periods_per_day); })
      .catch(() => {});
  }, []);

  const fetchPortalData = async () => {
    try {
      const res = await axios.get(`${API}/teacher/portal`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (error) {
      if (error.response?.status !== 403) console.error('Failed to load portal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeRelief = async (id) => {
    try {
      await axios.patch(`${API}/relief/${id}/acknowledge`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Relief duty acknowledged');
      fetchPortalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge');
    }
  };

  const acknowledgeInvigilation = async (id) => {
    try {
      await axios.patch(`${API}/invigilation/${id}/acknowledge`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invigilation duty acknowledged');
      fetchPortalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge');
    }
  };

  if (loading) return <div className="text-slate-600">Loading your portal...</div>;

  const timetableLookup = {};
  data?.timetable?.forEach(e => { timetableLookup[`${e.day}-${e.period}`] = e; });

  const totalPending = (data?.pending_relief?.length || 0) + (data?.pending_invigilation?.length || 0);

  const statCards = [
    { key: 'pending', title: 'Pending Duties', value: totalPending, icon: totalPending > 0 ? AlertTriangle : CheckCircle2, color: totalPending > 0 ? 'text-rose-500' : 'text-emerald-500', bg: totalPending > 0 ? 'bg-rose-50' : 'bg-emerald-50', testid: 'stat-pending-duties' },
    { key: 'upcoming_relief', title: 'Upcoming Relief', value: data?.upcoming_relief?.length || 0, icon: ClipboardList, color: 'text-blue-600', bg: 'bg-blue-50', testid: 'stat-upcoming-relief' },
    { key: 'upcoming_invigilation', title: 'Invigilation', value: data?.upcoming_invigilation?.length || 0, icon: CalendarDays, color: 'text-slate-600', bg: 'bg-slate-100', testid: 'stat-invigilation' },
    { key: 'workload', title: 'Week Workload', value: `${data?.workload?.current_week || 0}/${data?.workload?.effective_max || '-'}`, icon: BarChart3, color: 'text-emerald-600', bg: 'bg-emerald-50', testid: 'stat-workload' },
  ];

  return (
    <div data-testid="teacher-portal">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900">
          Welcome, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-sm sm:text-base text-slate-500 mt-2">Your schedule, duties, and workload at a glance</p>
      </div>

      {/* Top Stats Row - Interactive */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map(card => {
          const Icon = card.icon;
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => openDrill(card.key)}
              className="text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg"
              data-testid={card.testid}
            >
              <Card className="p-4 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer h-full">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{card.title}</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {card.key === 'workload'
                        ? <>{data?.workload?.current_week || 0}<span className="text-base font-normal text-slate-400">/{data?.workload?.effective_max || '-'}</span></>
                        : card.value}
                    </p>
                    <p className="mt-1.5 text-[10px] font-medium text-blue-700 inline-flex items-center gap-1">
                      View details <ArrowRight className="h-2.5 w-2.5" />
                    </p>
                  </div>
                  <div className={`p-2 rounded-md ${card.bg}`}>
                    <Icon className={`h-5 w-5 ${card.color}`} strokeWidth={1.5} />
                  </div>
                </div>
                {card.key === 'workload' && (
                  <div className="mt-2 w-full bg-slate-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all ${data?.workload?.remaining === 0 ? 'bg-rose-500' : data?.workload?.remaining <= 3 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(100, ((data?.workload?.current_week || 0) / (data?.workload?.effective_max || 25)) * 100)}%` }}
                    />
                  </div>
                )}
              </Card>
            </button>
          );
        })}
      </div>

      {/* Pending Duties - Prominent section */}
      {totalPending > 0 && (
        <Card className="p-4 sm:p-6 mb-6 border-rose-200 bg-rose-50/30" data-testid="pending-duties-section">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-rose-500" />
            Pending Acknowledgement ({totalPending})
          </h2>
          <div className="space-y-3">
            {data.pending_relief?.map(duty => (
              <div key={duty.id} className="flex items-center justify-between p-3 bg-white rounded-md border border-slate-200" data-testid={`pending-relief-${duty.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Relief</span>
                    <span className="text-sm font-medium text-slate-900">{duty.period} - {duty.class_name}</span>
                    <span className="text-sm text-slate-500">{duty.subject}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{formatDateShort(duty.date)} | Covering for {duty.absent_teacher_name}</p>
                </div>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 ml-3 shrink-0" onClick={() => acknowledgeRelief(duty.id)} data-testid={`ack-relief-${duty.id}`}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Acknowledge
                </Button>
              </div>
            ))}
            {data.pending_invigilation?.map(duty => (
              <div key={duty.id} className="flex items-center justify-between p-3 bg-white rounded-md border border-slate-200" data-testid={`pending-invig-${duty.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-slate-200 text-slate-700 text-xs font-medium rounded">Invigilation</span>
                    <span className="text-sm font-medium text-slate-900">{duty.venue}</span>
                    <span className="text-sm text-slate-500">{duty.time_slot}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{formatDateShort(duty.date)} | {duty.duty_type}</p>
                </div>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 ml-3 shrink-0" onClick={() => acknowledgeInvigilation(duty.id)} data-testid={`ack-invig-${duty.id}`}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Acknowledge
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Weekly Timetable Calendar Grid */}
      <Card className="p-4 sm:p-6 mb-6" data-testid="weekly-timetable-card">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-5 w-5 text-blue-900" strokeWidth={1.5} />
          <h2 className="text-lg font-semibold text-slate-900">My Weekly Timetable</h2>
        </div>
        {data?.timetable?.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">No timetable entries found. Ask your admin to import the timetable.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm border-collapse" data-testid="timetable-calendar-grid">
              <thead>
                <tr>
                  <th className="p-2 text-left font-medium text-slate-500 border-b border-slate-200 w-24"></th>
                  {days.map(day => (
                    <th key={day} className="p-2 text-center font-medium text-slate-500 border-b border-slate-200">{day}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periods.map(period => (
                  <tr key={period}>
                    <td className="p-2 font-medium text-slate-600 border-b border-slate-100 whitespace-nowrap">{period}</td>
                    {days.map(day => {
                      const entry = timetableLookup[`${day}-${period}`];
                      return (
                        <td key={day} className="p-1.5 border-b border-slate-100">
                          {entry ? (
                            <div className={`px-2 py-1.5 rounded border text-center ${getSubjectColor(entry.subject)}`}>
                              <p className="font-semibold text-xs">{entry.class_name}</p>
                              <p className="text-xs opacity-80">{entry.subject}</p>
                              {entry.time_slot && <p className="text-xs opacity-60 mt-0.5">{entry.time_slot}</p>}
                            </div>
                          ) : (
                            <div className="px-2 py-1.5 text-center text-slate-300">-</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Upcoming Duties */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-4 sm:p-6" data-testid="upcoming-relief-card">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
            Upcoming Relief Duties
          </h2>
          {data?.upcoming_relief?.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No upcoming relief duties</p>
          ) : (
            <div className="space-y-2">
              {data.upcoming_relief.map(duty => (
                <div key={duty.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-200" data-testid={`upcoming-relief-${duty.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{duty.period} - {duty.class_name} ({duty.subject})</p>
                    <p className="text-xs text-slate-500">{formatDateShort(duty.date)} | Covering for {duty.absent_teacher_name}</p>
                  </div>
                  {duty.is_acknowledged ? (
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Ack'd
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => acknowledgeRelief(duty.id)}>
                      Acknowledge
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4 sm:p-6" data-testid="upcoming-invigilation-card">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-slate-600" strokeWidth={1.5} />
            Upcoming Invigilation
          </h2>
          {data?.upcoming_invigilation?.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No upcoming invigilation duties</p>
          ) : (
            <div className="space-y-2">
              {data.upcoming_invigilation.map(duty => (
                <div key={duty.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-200" data-testid={`upcoming-invig-${duty.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{duty.venue} - {duty.time_slot}</p>
                    <p className="text-xs text-slate-500">{formatDateShort(duty.date)} | {duty.duty_type}</p>
                  </div>
                  {duty.is_acknowledged ? (
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Ack'd
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => acknowledgeInvigilation(duty.id)}>
                      Acknowledge
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Drill-down Dialog */}
      <Dialog open={drilldown.open} onOpenChange={(open) => setDrilldown({ open, card: open ? drilldown.card : null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="teacher-drilldown-dialog">
          <DialogHeader>
            <DialogTitle>
              {drilldown.card === 'pending' && `Pending Duties (${totalPending})`}
              {drilldown.card === 'upcoming_relief' && `Upcoming Relief Duties (${data?.upcoming_relief?.length || 0})`}
              {drilldown.card === 'upcoming_invigilation' && `Upcoming Invigilation (${data?.upcoming_invigilation?.length || 0})`}
              {drilldown.card === 'workload' && 'Week Workload Breakdown'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Click-through detail for this stat card.</DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            {drilldown.card === 'pending' && (
              <>
                {totalPending === 0 && <p className="text-sm text-slate-400 italic py-6 text-center">All duties acknowledged.</p>}
                {data?.pending_relief?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-rose-700 mb-2">Relief — {data.pending_relief.length}</p>
                    <div className="space-y-2">
                      {data.pending_relief.map(duty => (
                        <div key={duty.id} className="p-3 bg-rose-50/60 border border-rose-100 rounded-md flex justify-between items-center gap-3" data-testid={`drill-pending-relief-${duty.id}`}>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{duty.period} · {duty.class_name}</p>
                            <p className="text-xs text-slate-500">{formatDateShort(duty.date)} · Covering {duty.absent_teacher_name} · {duty.subject}</p>
                          </div>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => acknowledgeRelief(duty.id)}>
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Acknowledge
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {data?.pending_invigilation?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-rose-700 mb-2">Invigilation — {data.pending_invigilation.length}</p>
                    <div className="space-y-2">
                      {data.pending_invigilation.map(duty => (
                        <div key={duty.id} className="p-3 bg-rose-50/60 border border-rose-100 rounded-md flex justify-between items-center gap-3" data-testid={`drill-pending-invig-${duty.id}`}>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{duty.venue} · {duty.time_slot}</p>
                            <p className="text-xs text-slate-500">{formatDateShort(duty.date)} · {duty.duty_type}</p>
                          </div>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => acknowledgeInvigilation(duty.id)}>
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Acknowledge
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {drilldown.card === 'upcoming_relief' && (
              <>
                {(data?.upcoming_relief || []).length === 0 && <p className="text-sm text-slate-400 italic py-6 text-center">No upcoming relief duties.</p>}
                {(data?.upcoming_relief || []).map(duty => (
                  <div key={duty.id} className="p-3 bg-slate-50 border border-slate-200 rounded-md flex justify-between items-center gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{duty.period} · {duty.class_name}</p>
                      <p className="text-xs text-slate-500">{formatDateLong(duty.date)} · Covering {duty.absent_teacher_name} · {duty.subject}</p>
                    </div>
                    {duty.is_acknowledged ? (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Ack'd</span>
                    ) : (
                      <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => acknowledgeRelief(duty.id)}>Acknowledge</Button>
                    )}
                  </div>
                ))}
              </>
            )}

            {drilldown.card === 'upcoming_invigilation' && (
              <>
                {(data?.upcoming_invigilation || []).length === 0 && <p className="text-sm text-slate-400 italic py-6 text-center">No upcoming invigilation duties.</p>}
                {(data?.upcoming_invigilation || []).map(duty => (
                  <div key={duty.id} className="p-3 bg-slate-50 border border-slate-200 rounded-md flex justify-between items-center gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{duty.venue} · {duty.time_slot}</p>
                      <p className="text-xs text-slate-500">{formatDateLong(duty.date)} · {duty.duty_type}</p>
                    </div>
                    {duty.is_acknowledged ? (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Ack'd</span>
                    ) : (
                      <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => acknowledgeInvigilation(duty.id)}>Acknowledge</Button>
                    )}
                  </div>
                ))}
              </>
            )}

            {drilldown.card === 'workload' && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-md text-center">
                    <p className="text-[11px] font-semibold uppercase text-emerald-700 tracking-wider mb-1">This Week</p>
                    <p className="text-2xl font-bold text-emerald-900">{data?.workload?.current_week || 0}</p>
                    <p className="text-[11px] text-slate-500">periods taught</p>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-center">
                    <p className="text-[11px] font-semibold uppercase text-slate-600 tracking-wider mb-1">Weekly Max</p>
                    <p className="text-2xl font-bold text-slate-900">{data?.workload?.effective_max || '-'}</p>
                    <p className="text-[11px] text-slate-500">role-based cap</p>
                  </div>
                  <div className={`p-3 border rounded-md text-center ${data?.workload?.remaining === 0 ? 'bg-rose-50 border-rose-100' : data?.workload?.remaining <= 3 ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100'}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-wider mb-1 text-slate-600">Remaining</p>
                    <p className="text-2xl font-bold text-slate-900">{data?.workload?.remaining ?? '-'}</p>
                    <p className="text-[11px] text-slate-500">before cap</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 italic">
                  Workload cap comes from your role / job-title configuration. Exco / HOD roles may have reduced caps to protect leadership time.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* SchoolzestAI Chat */}
      <div className="mt-6">
        <SchoolzestAIWidget />
      </div>
    </div>
  );
};

export default TeacherPortal;
